import React, { useEffect, useRef, useMemo } from 'react';
import { drawInitialMap, cleanupMap } from 'utils/apple-maps';
import { useThemeContext } from 'contexts';
import { isClientSide } from 'utils/host-config';

import type AppleMap from '@zoocasa/node-kit/map/apple/map';
import type { GeoJsonPoint } from '@zoocasa/node-kit/map/types';
import type { PropsWithTestId } from 'types';
import type { PropsWithChildren } from 'react';

type BaseMapProps = PropsWithChildren<PropsWithTestId<{
  position: GeoJsonPoint;
  mapElementId: string;
  isInteractive?: boolean;
  renderPin?: boolean;
  shouldLoad?: boolean;
  address?: string;
  className?: string;
  onMapLoaded?: (mapInstance: AppleMap) => void;
  onError?: (error: unknown) => void;
}>>;

function BaseMap({
  position,
  mapElementId,
  isInteractive = true,
  renderPin = true,
  shouldLoad = true,
  address,
  className,
  children,
  onMapLoaded,
  onError,
  testId,
}: BaseMapProps) {
  const mapRef = useRef<AppleMap | null>(null);
  const { themeName } = useThemeContext();

  const [longitude, latitude] = position.coordinates;
  const stablePosition = useMemo(() => ({
    type: 'Point',
    coordinates: [longitude, latitude],
  }), [longitude, latitude]);

  useEffect(() => {
    if (shouldLoad && mapRef.current === null && isClientSide()) {
      const initializeMap = async () => {
        // Check if element exists and is visible
        const element = document.getElementById(mapElementId);
        if (!element) {
          return;
        }

        // Check if map is already being initialized (race condition check)
        if (element.dataset.initializing === 'true') {
          return;
        }

        // Mark as initializing to prevent race conditions
        element.dataset.initializing = 'true';

        try {
          const mapInstance = await drawInitialMap(
            mapElementId,
            stablePosition,
            isInteractive,
            themeName,
            renderPin
          );

          mapRef.current = mapInstance;

          // Check if location needs to be adjusted
          const [longitude, latitude] = stablePosition.coordinates;
          if (mapRef.current.longitude === 0 || mapRef.current.latitude === 0) {
            mapRef.current.changeLocation({ lat: latitude, lng: longitude }, 14);
          }

          onMapLoaded?.(mapInstance);

        } catch (error) {
          console.error('BaseMap: Map initialization failed', error);
          onError?.(error);
        } finally {
          // Clear the initializing flag
          element.dataset.initializing = 'false';
        }
      };

      initializeMap();
    }

    return () => {
      if (mapRef.current) {
        mapRef.current?.removeEventListeners();
        cleanupMap(mapElementId);
        mapRef.current = null;
      }
    };
  }, [shouldLoad, mapElementId, stablePosition, isInteractive, themeName, renderPin, onMapLoaded, onError]);

  return (
    <div className="map-container">
      {/* SEO-friendly static content */}
      {address && (
        <address
          itemScope
          itemType="https://schema.org/Place"
          style={{ display: 'none' }}
        >
          <span itemProp="name">{address}</span>
          <div itemProp="geo" itemScope itemType="https://schema.org/GeoCoordinates">
            <meta itemProp="latitude" content={stablePosition.coordinates[1].toString()} />
            <meta itemProp="longitude" content={stablePosition.coordinates[0].toString()} />
          </div>
        </address>
      )}

      {/* Interactive map */}
      {children}
      <div id={mapElementId} className={className} data-testid={testId} />
    </div>
  );
}

export default BaseMap;
export { type BaseMapProps };